import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { MotionProvider } from './components/MotionProvider';
import { applyUiOverrides, getPreferredLanguage, loadLocale } from './i18n';
import { api } from './lib/api';
import { isPanelHost } from './lib/host';
import { startOfflineSync } from './lib/offline-queue';
import { invalidateActivity, invalidateDerived } from './lib/query-keys';
import { clearPersistedCache, restoreCache, startCachePersistence } from './lib/query-persist';
import { router } from './router';
import './styles/globals.css';

// On respiriapanel.isaies.com the bundle is just the admin panel; on the app
// host it's never rendered, so keep it out of the main chunk (issue #38 §3).
const AdminPage = lazy(() => import('./features/admin/AdminPage').then((m) => ({ default: m.AdminPage })));

function PanelFallback() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-brand" />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Offline-first: keep showing data instead of blanking the screen, and
      // don't hammer the network on every focus change.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
      networkMode: 'offlineFirst',
    },
  },
});

// Replays anything logged while offline, then refreshes what it affected —
// activity (check-ins, doses…) and derived state (settings/auth/money…), so a
// settings change made offline reconciles with the server after syncing.
startOfflineSync(() => {
  invalidateActivity(queryClient);
  invalidateDerived(queryClient);
});

// Editable UI translations (issue #14): fetch the server overrides once and layer
// them over the static catalogue. Fire-and-forget — consumers re-render via the
// override store — and never let it block startup or fail loudly (offline just
// keeps the static catalogue).
void api<{ overrides: Record<string, Record<string, string>> }>('/api/i18n/overrides')
  .then((data) => applyUiOverrides(data.overrides))
  .catch(() => {});

// On the panel host, swap the favicon for the darker-blue variant so its tab is
// easy to tell apart from the (green) app tab.
if (isPanelHost()) {
  const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (icon) icon.href = '/favicon-panel.svg';
  document.title = 'Respiria · Panel';
}

// On respiriapanel.isaies.com the whole bundle is just the admin panel — no
// router, no tab bar, no dashboard. Everything else is the normal app.
function render() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <MotionProvider>
          {isPanelHost() ? (
            <Suspense fallback={<PanelFallback />}>
              <AdminPage />
            </Suspense>
          ) : (
            <RouterProvider router={router} />
          )}
        </MotionProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

// Offline reads: bring back the last cache before first paint so a slow or absent
// connection shows data instantly, then keep persisting it as it changes. Render
// after restoring (a few ms), and never let a cache problem block startup.
// Load the chosen language's strings in parallel so a non-Spanish user doesn't
// flash Spanish on first paint (issue #38 §3). (No top-level await: the build
// target doesn't support it.)
// Arriving with `?switched=1` means we just changed account (admin impersonation
// from the panel, cross-origin). Purge the persisted cache instead of restoring
// it, so the boot doesn't rehydrate the previous user's data, and drop the flag.
const switched = new URLSearchParams(window.location.search).has('switched');
if (switched) {
  const url = new URL(window.location.href);
  url.searchParams.delete('switched');
  window.history.replaceState(window.history.state, '', url.toString());
}
const restoreOrPurge = switched ? clearPersistedCache() : restoreCache(queryClient);

Promise.allSettled([restoreOrPurge, loadLocale(getPreferredLanguage())]).finally(() => {
  startCachePersistence(queryClient);
  render();
});
